<template>
	<div class="profile-show">
		<div class="inbox-title sticky sticky top-0 settings-title-container">
			<h1 class="inbox-title-label">
				{{ name }}
			</h1>
		</div>

		<div class="settings-header">
			<div class="settings-header__left"></div>

			<div class="settings-header__right">
				<div
					class="settings-header__right-container"
					@click.prevent="onScheduleClick"
				>
					<span class="settings-header-btn-text cursor-pointer"
						>Set Office Hours</span
					>
					<button
						class="settings-header-btn"
						data-tooltip="Set Office Hours"
						aria-label="Set Office Hours"
						data-position="bottom-left"
					>
						<font-awesome-icon
							fixed-width
							:icon="['fal', 'clock']"
							aria-hidden="true"
						/>
					</button>
				</div>
			</div>
		</div>
		<form
			v-if="partner"
			class="profile-container"
			@submit.prevent="onSubmit"
		>
			<div class="tab-container" @submit.prevent="onSubmit">
				<div class="tabs">
					<button
						v-for="tab in visibleTabs"
						:key="tab.name"
						type="button"
						:class="[
							'tab',
							{
								active: activeTab === tab.name,
								disabled: isTabDisabled(tab.name),
							},
						]"
						:style="{ width: `${100 / visibleTabs.length}%` }"
						:disabled="isTabDisabled(tab.name)"
						:data-tooltip="
							isTabDisabled(tab.name)
								? 'Please fill the required fields for the current tab.'
								: null
						"
						data-position="bottom"
						@click="selectTab(tab.name)"
					>
						{{ tab.label }}
					</button>
				</div>

				<!-- Tab Content for Configuration -->
				<div v-show="activeTab === 'config'" class="tab-content">
					<div class="card">
						<div class="card-title">
							<div class="card-title_left-setting-page">
								Configuration
							</div>
							<div class="card-title_right-setting-page">
								Address
							</div>
						</div>
						<div class="card-body">
							<div class="card-body_left-setting-page">
								<div class="card-body-container">
									<div class="form-group">
										<label
											class="form-label form-group-title-new"
										>
											Practice Name
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{ form.name }}
										</div>
									</div>

									<div class="form-group">
										<label
											class="form-label form-group-title-new"
										>
											Web Display Name
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{
												form.wma_display_name ||
													form.name
											}}
										</div>
									</div>

									<div
										v-if="isWMAEnabledPartner"
										class="form-group"
									>
										<label for="wma_web_link">
											WMA Weblink
										</label>
										<div class="flex flex-row items-center">
											<a
												:href="form.wma_web_link"
												target="_blank"
												class="nonEditable-text settings-input hover:no-underline"
											>
												{{ form.wma_web_link }}
											</a>
											<button
												v-if="form.wma_web_link"
												data-tooltip="Copy WMA Weblink"
												data-position="top"
												class="form-copy-icon"
												@click.prevent="onCopyWMALink"
											>
												<font-awesome-icon
													fixed-width
													:icon="['far', 'clone']"
													:rotation="90"
												/>
											</button>
										</div>
									</div>

									<div class="form-group">
										<label
											class="form-label form-group-title-new"
										>
											Forwarding Number
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{
												partner.twilio_phone_number
													| newFormatPhoneNumber
											}}
										</div>
									</div>

									<div
										class="profile-section-single-line-details"
									>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="office_phone_number"
												class="form-label form-group-title-new"
											>
												Office Phone Number
											</label>
											<input
												id="office_phone_number"
												v-model="rawOfficePhoneNumber"
												type="text"
												name="office_phone_number"
												class="form-input"
												placeholder="Phone number"
												@input="
													validateOfficePhoneNumber
												"
											/>
											<div
												v-if="officeNumberErrorMessage"
												class="text-red-500 text-sm mt-2"
											>
												{{ officeNumberErrorMessage }}
											</div>
										</div>

										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="office_fax_number"
												class="form-label form-group-title-new"
											>
												Office Fax Number
											</label>
											<input
												id="office_fax_number"
												v-model="rawOfficeFaxNumber"
												type="text"
												name="office_fax_number"
												placeholder="Fax number"
												class="form-input"
											/>
										</div>
									</div>

									<div class="form-group">
										<label for="fallback_email_1">
											Fallback Notification Email
										</label>
										<input
											id="fallback_email_1"
											v-model="form.fallback_email"
											type="text"
											name="fallback_email_1"
											class="form-input"
											placeholder="Fallback notification email"
										/>
									</div>
									<div
										class="profile-section-single-line-details"
									>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="fallback_phone_number"
												class="form-label form-group-title-new"
											>
												Fallback Notification Phone
												Number
											</label>
											<input
												id="fallback_phone_number"
												v-model="rawFallbackPhoneNumber"
												type="text"
												name="fallback_phone_number"
												class="form-input"
												placeholder="Notification phone number"
												@input="
													validateFallbackPhoneNumber
												"
											/>
											<div
												v-if="
													fallbackNumberErrorMessage
												"
												class="text-red-500 text-sm mt-2"
											>
												{{ fallbackNumberErrorMessage }}
											</div>
										</div>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="timezone"
												class="form-label form-group-title-new"
											>
												Timezone
											</label>
											<div class="relative">
												<select
													id="timezone"
													v-model="form.timezone"
													name="timezone"
													placeholder="Select a timezone"
													class="form-input"
													:class="[
														form.timezone
															? 'text-gray-700'
															: 'text-gray-500',
													]"
												>
													<option
														:value="null"
														disabled
														>Select a
														timezone</option
													>
													<option
														v-for="timezone in timezones"
														:key="timezone"
														:value="timezone"
													>
														{{ timezone }}
													</option>
												</select>
												<div
													class="pointer-events-none absolute inset-y-0 mb-6 right-0 flex items-center px-2 text-gray-700"
												>
													<font-awesome-icon
														class="fill-current h-4 w-4 mt-4 mr-2"
														:icon="[
															'far',
															'chevron-down',
														]"
													></font-awesome-icon>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="card-body_right-setting-page">
								<div class="card-body-container">
									<div class="form-group">
										<label
											for="address_1"
											class="form-label form-group-title-new"
										>
											Street Address
										</label>
										<input
											id="address_1"
											v-model="form.address_1"
											type="text"
											name="address_1"
											class="form-input"
											placeholder="Street address"
										/>
									</div>
									<div class="form-group">
										<label
											for="address_2"
											class="form-label form-group-title-new"
										>
											Address Line 2
										</label>
										<input
											id="address_2"
											v-model="form.address_2"
											type="text"
											name="address_2"
											class="form-input"
											placeholder="Address line 2"
										/>
									</div>
									<div
										class="profile-section-single-line-details"
									>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="city"
												class="form-label form-group-title-new"
											>
												City
											</label>
											<input
												id="city"
												v-model="form.city"
												type="text"
												name="city"
												class="form-input"
												placeholder="City"
											/>
										</div>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="state_province"
												class="form-label form-group-title-new"
											>
												State Or Province
											</label>
											<input
												id="state_province"
												v-model="form.state_province"
												type="text"
												name="state_province"
												class="form-input"
												placeholder="State or Province"
											/>
										</div>
									</div>
									<div
										class="profile-section-single-line-details"
									>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="country"
												class="form-label form-group-title-new"
											>
												Country
											</label>
											<input
												id="country"
												v-model="form.country"
												type="text"
												name="country"
												class="form-input"
												placeholder="Country"
											/>
										</div>
										<div
											class="form-group form-group-profile-section-single-line-details"
										>
											<label
												for="postal_code"
												class="form-label form-group-title-new"
											>
												Postal Code
											</label>
											<input
												id="postal_code"
												v-model="form.postal_code"
												type="text"
												name="postal_code"
												class="form-input"
												autocomplete="postal-code"
												placeholder="Postal code"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div class="card-footer">
							<div class="card-footer_left"></div>
							<div class="card-footer_right">
								<button
									class="action-button"
									type="submit"
									data-tooltip="Save"
									data-position="bottom"
									data-cy="save-button"
								>
									Save
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Tab Content for Voicemail Configuration -->
				<div v-show="activeTab === 'voicemail'" class="tab-content">
					<div class="card">
						<div class="card-title">
							Secondary Voicemail
						</div>
						<div class="card-body">
							<div class="card-body-container">
								<div class="icon-container">
									<span class="space-x-4">
										<button
											type="button"
											aria-label="Filter reports"
											data-position="top"
											data-tooltip="Help"
											@click.stop.prevent="onClickInfo"
										>
											<font-awesome-icon
												class="address-icon"
												:icon="[
													'far',
													'question-circle',
												]"
											/>
										</button>
										<button
											type="button"
											aria-label="Filter reports"
											data-position="top"
											data-tooltip="Copy Settings"
											@click.stop.prevent="
												copyVoicemailData
											"
										>
											<font-awesome-icon
												class="address-icon"
												:icon="['far', 'address-card']"
											/>
										</button>
									</span>
								</div>
								<div
									class="voicemail-section-single-line-details mb-2"
								>
									<div
										class="form-group form-group-voicemail-section-single-line-details mb-3"
									>
										<label class="form-label mb-0">
											Voicemail Retrieval Number
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{
												partner.global_rsm_phone_number
													| newFormatPhoneNumber
											}}
										</div>
									</div>
									<div
										class="form-group form-group-voicemail-section-single-line-details"
									>
										<label class="form-label mb-0">
											Mailbox Number
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{
												partner.twilio_phone_number
													| newFormatPhoneNumber
											}}
										</div>
									</div>
									<div
										class="form-group form-group-voicemail-section-single-line-details"
									>
										<label
											for="pass_code"
											class="form-label mb-0"
											>Passcode</label
										>
										<div class="relative">
											<input
												id="pass_code"
												v-model="form.passcode"
												autocomplete="new-password"
												:type="
													showPasscode
														? 'text'
														: 'password'
												"
												name="pass_code"
												class="form-input"
												placeholder="Passcode"
												maxlength="6"
												required
												@input="validatePasscode"
											/>
											<button
												v-if="shouldShowEyeButtonRSM"
												type="button"
												data-tooltip="Show Passcode"
												class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-700"
												@click="
													showPasscode = !showPasscode
												"
											>
												<font-awesome-icon
													:icon="[
														showPasscode
															? 'fas'
															: 'fas',
														showPasscode
															? 'eye-slash'
															: 'eye',
													]"
												/>
											</button>
										</div>
										<div
											v-if="passcodeErrorMessage"
											class="text-red-500 text-sm mt-2"
										>
											{{ passcodeErrorMessage }}
										</div>
									</div>
								</div>
								<div class="form-group">
									<label
										for="email_notification"
										class="form-label ml-1"
									>
										Email Notifications
									</label>
									<div class="input-tag-container">
										<div
											class="tag-container"
											:class="{
												empty:
													fallbackEmailTags.length ===
													0,
											}"
										>
											<div
												v-for="(email,
												index) in fallbackEmailTags"
												:key="index"
												class="tag"
											>
												{{ email }}
												<span
													class="close mr-1"
													@click="removeEmail(index)"
													>x</span
												>
											</div>
											<input
												id="email_notifications"
												v-model="newEmail"
												type="email"
												class="form-input-fallback_email"
												:placeholder="
													fallbackEmailTags.length ===
													0
														? 'Add Email...'
														: 'Add Emails...'
												"
												@keydown.enter.prevent="
													addEmail
												"
												@keydown="handleBackspace"
												@keypress="
													checkForComma($event)
												"
												@input="
													debounceFilterEmailSuggestions(
														$event
													)
												"
												@blur="addEmail"
											/>
										</div>
										<ul
											v-if="emailSuggestions.length"
											:class="[
												'email-suggestions',
												{
													'open-upwards': shouldOpenUpwards,
												},
											]"
										>
											<li
												v-for="suggestion in emailSuggestions"
												:key="suggestion"
												class="suggestion-item"
												@mousedown="
													selectEmail(suggestion)
												"
											>
												{{ suggestion }}
											</li>
										</ul>
									</div>
									<div
										v-if="emailErrorMessage"
										class="text-red-500 text-sm"
									>
										{{ emailErrorMessage }}
									</div>
								</div>
							</div>
						</div>
						<div class="card-footer">
							<div class="card-footer_left"></div>
							<div class="card-footer_right">
								<button
									class="action-button"
									type="submit"
									data-tooltip="Save"
									data-position="bottom"
									data-cy="save-button"
								>
									Save
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Tab Content for EHR Configuration -->
				<div
					v-show="activeTab === 'ehr'"
					v-if="isEhrConfigured && isPartnerAdmin"
					class="tab-content"
				>
					<div>
						<div class="card">
							<div class="card-title">
								Configuration
							</div>
							<div class="card-body">
								<div class="card-body-container">
									<div
										class="form-group form-group-voicemail-section-single-line-details-test"
									>
										<label class="form-label mb-0">
											Provider
										</label>
										<div
											class="nonEditable-text settings-input"
										>
											{{ ehrProviderName }}
										</div>
									</div>
									<div
										class="voicemail-section-single-line-details mb-2"
									>
										<div
											class="form-group form-group-voicemail-section-single-line-details"
										>
											<label
												for="username"
												class="form-label form-group-title-new"
											>
												Username
											</label>
											<div class="relative">
												<input
													id="username"
													v-model="
														form.ehr_config.username
													"
													type="text"
													name="username"
													placeholder="Username"
													class="form-input"
													style="padding-right: 2rem;"
												/>
												<button
													type="button"
													style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: black; font-size: 1rem; background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center;"
													@click="
														form.ehr_config.username =
															''
													"
												>
													<font-awesome-icon
														fixed-width
														class="btn-icon text-gray-700 new-design-icon-sm"
														:icon="['fal', 'times']"
														style="padding-top: 0.10rem;"
													/>
												</button>
											</div>
										</div>
										<div
											class="form-group form-group-voicemail-section-single-line-details"
										>
											<label
												for="pass_word"
												class="form-label mb-0"
												>Password</label
											>
											<div class="relative">
												<input
													id="pass_word"
													v-model="
														form.ehr_config.password
													"
													autocomplete="new-password"
													:type="
														showPasscode
															? 'text'
															: 'password'
													"
													name="pass_word"
													class="form-input"
													placeholder="Password"
												/>
												<button
													v-if="
														shouldShowEyeButtonEHR
													"
													type="button"
													data-tooltip="Show Passcode"
													class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-700"
													@click="
														showPasscode = !showPasscode
													"
												>
													<font-awesome-icon
														:icon="[
															showPasscode
																? 'fas'
																: 'fas',
															showPasscode
																? 'eye-slash'
																: 'eye',
														]"
													/>
												</button>
											</div>
										</div>

										<div
											class="form-group form-group-voicemail-section-single-line-details"
										>
											<label
												for="destination_id"
												class="form-label form-group-title-new"
											>
												Destination ID
											</label>
											<div class="relative">
												<input
													id="destination_id"
													v-model="
														form.ehr_config
															.destination_id
													"
													type="text"
													name="destination_id"
													placeholder="Destination ID"
													class="form-input"
													style="padding-right: 2rem;"
												/>
												<button
													type="button"
													style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: black; font-size: 1rem; background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center;"
													@click="
														form.ehr_config.destination_id =
															''
													"
												>
													<font-awesome-icon
														fixed-width
														class="btn-icon text-gray-700 new-design-icon-sm"
														:icon="['fal', 'times']"
														style="padding-top: 0.10rem;"
													/>
												</button>
											</div>
										</div>
									</div>

									<!-- New Fields -->
									<div
										class="voicemail-section-single-line-details mb-2"
									>
										<div
											class="form-group form-group-voicemail-section-single-line-details"
										>
											<label
												for="service_application_id"
												class="form-label form-group-title-new"
											>
												Application ID
											</label>
											<div class="relative">
												<input
													id="service_application_id"
													v-model="
														form.ehr_config
															.service_application_id
													"
													type="text"
													name="service_application_id"
													placeholder="Application ID"
													class="form-input"
													style="padding-right: 2rem;"
												/>
												<button
													type="button"
													style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: black; font-size: 1rem; background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center;"
													@click="
														form.ehr_config.service_application_id =
															''
													"
												>
													<font-awesome-icon
														fixed-width
														class="btn-icon text-gray-700 new-design-icon-sm"
														:icon="['fal', 'times']"
														style="padding-top: 0.10rem;"
													/>
												</button>
											</div>
										</div>

										<div
											class="form-group form-group-voicemail-section-single-line-details"
										>
											<label
												for="service_facility_id"
												class="form-label form-group-title-new"
											>
												Facility ID
											</label>
											<div class="relative">
												<input
													id="service_facility_id"
													v-model="
														form.ehr_config
															.service_facility_id
													"
													type="text"
													name="service_facility_id"
													placeholder="Facility ID"
													class="form-input"
													style="padding-right: 2rem;"
												/>
												<button
													type="button"
													style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: black; font-size: 1rem; background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center;"
													@click="
														form.ehr_config.service_facility_id =
															''
													"
												>
													<font-awesome-icon
														fixed-width
														class="btn-icon text-gray-700 new-design-icon-sm"
														:icon="['fal', 'times']"
														style="padding-top: 0.10rem;"
													/>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="card-footer">
								<div class="card-footer_left"></div>
								<div class="card-footer_right">
									<button
										class="action-button"
										type="submit"
										:data-tooltip="
											!isSaveDisabled ? 'Save' : null
										"
										data-position="bottom"
										data-cy="save-button"
										:disabled="isSaveDisabled"
										@click="clearCurrentNoteTitle"
									>
										Save
									</button>
								</div>
							</div>
						</div>
						<div v-if="shouldShowNoteTypes" class="card">
							<div class="card-title">
								Configure Note Types
							</div>
							<div class="card-body">
								<div
									class="card-body-container note-type-container"
								>
									<div class="coordination-note-container ">
										<div
											class="coordination-note-container_left"
										>
											<div
												class="coordination-note-check-box-container"
											>
												<div
													v-for="note in sortedNoteTypesWithSavedSelectedFirst"
													:key="note.id"
													class="note-type-item"
													@click="
														onClickNoteType(note)
													"
												>
													<div
														class="custom-checkbox"
													>
														<input
															:id="
																`note-${note.id}`
															"
															v-model="
																selectedValues
															"
															type="checkbox"
															:value="note.id"
															:checked="
																isChecked(
																	note.id
																)
															"
															@change="
																onNoteTypeSelected(
																	note
																)
															"
														/>
														<label
															:for="
																`note-${note.id}`
															"
														></label>
													</div>

													<!-- Updated label with the note title -->
													<label class="note-label">
														{{ note.title }}
													</label>
												</div>
											</div>
										</div>
										<div
											class="coordination-note-container_right"
										>
											<div class="note-item-name">
												<div
													v-if="
														currentNoteTitle ||
															shouldShowNoteTypesStaticBox
													"
												>
													{{
														currentNoteTitle ||
															firstNoteTitleSelected
													}}
												</div>
											</div>
											<!-- Empty for now -->
											<div class="note-item-details">
												<div
													v-if="
														shouldShowNoteTypesStaticBox ||
															currentNoteTitle
													"
													class="note-item-field"
												>
													<label class="field-label"
														>Call Received:</label
													>
													<label class="field-label">
														%call-ended%
													</label>
													---
													<label class="field-label">
														Message:
													</label>
													<label class="field-label">
														"%message%"
													</label>
													---
													<label class="field-label">
														Notes:
													</label>
													<label class="field-label">
														%notes%
													</label>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="card-footer">
								<div class="card-footer_left">
									<span v-if="showNoteWarning" class="warning"
										>At least one note type must be
										selected.</span
									>
								</div>
								<div class="card-footer_right">
									<button
										class="action-button"
										type="submit"
										data-tooltip="Save"
										data-position="bottom"
										data-cy="save-button"
									>
										Save
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
		<schedule-panel :show="panel" @close="panel = false" />
	</div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapState } from 'vuex'

import SchedulePanel from '@/components/Panels/SchedulePanel.vue'
import intercomConfig from '@/config/intercom'
import _get from 'lodash/get'
import util from '@/util'
import {
	WMA_WEBLINK_ERROR_MESSAGE,
	WMA_WEBLINK_SUCCESS_MESSAGE,
} from '@/config/constants'

/**
 * Get the empty partner form object.
 *
 * @return {Object}
 */
const getFormObject = () => {
	return {
		name: '',
		wma_display_name: '',
		wma_web_link: '',
		office_phone_number: '',
		office_fax_number: '',
		address_1: '',
		address_2: '',
		city: '',
		state_province: '',
		country: '',
		postal_code: '',
		fallback_email: '',
		fallback_phone_number: '',
		timezone: '',
		rsm_fallback_email: '',
		passcode: '',
		ehr_config: {
			username: '',
			password: '',
			destination_id: '',
			service_application_id: '',
			service_facility_id: '',
			note_types: '',
			metadata: '', // Adjust this type if it's not a simple string, after BE, PHASE 2
		},
	}
}

export default {
	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		SchedulePanel,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		rawOfficeFaxNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(this.form.office_fax_number)
			},
			set(value) {
				this.form.office_fax_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		rawOfficePhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(
					this.form.office_phone_number
				)
			},
			set(value) {
				this.form.office_phone_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		rawFallbackPhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(
					this.form.fallback_phone_number
				)
			},
			set(value) {
				this.form.fallback_phone_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		sortedNoteTypesWithSavedSelectedFirst() {
			// Get selected note types based on savedSelectedValues
			const selectedNoteTypes = this.sortedNoteTypes.filter(note =>
				this.savedSelectedValues.includes(note.id)
			)

			// Get the remaining note types that are not selected
			const remainingNoteTypes = this.sortedNoteTypes.filter(
				note => !this.savedSelectedValues.includes(note.id)
			)

			// Return saved selected types first, followed by remaining types
			return [...selectedNoteTypes, ...remainingNoteTypes]
		},

		noteTitleToShow() {
			// Find the first selected note from savedSelectedValues
			const firstSelectedNote = this.sortedNoteTypes.find(note =>
				this.savedSelectedValues.includes(note.id)
			)

			// If a note is found, return its title, otherwise return an empty string
			return firstSelectedNote ? firstSelectedNote.title : ''
		},

		shouldShowNoteTypesStaticBox() {
			// Check if there are any selected note types
			return this.selectedValues.length > 0
		},

		shouldShowEyeButtonEHR() {
			const fromPasswordVal = this.form.ehr_config.password
			if (fromPasswordVal.length === 0) {
				return false
			}

			if (this.wasCleared) {
				return true
			}

			return false
		},

		shouldShowEyeButtonRSM() {
			const fromPasswordVal = this.form.passcode

			if (fromPasswordVal.length === 0) {
				return false
			}

			if (this.wasCleared) {
				return true
			}

			return false
		},

		/**
		 * Determine if the user has the partner admin role or global admin
		 *
		 * @return {Boolean}
		 */
		isPartnerAdmin() {
			const roles = _get(this.$store.state, 'auth.user.roles', [])
			const activePartnerId = _get(
				this.$store.getters.partner,
				'id',
				null
			)

			const isAdminRole = roles.some(role => role.name === 'admin')

			if (isAdminRole) {
				return true
			}

			const isPartnerAdminRole = roles.some(
				role =>
					role.name === 'partner-admin' &&
					role.is_partner_role === true &&
					role.partner_id === activePartnerId
			)

			return isPartnerAdminRole
		},

		/**
		 * Returns the EHR provider's name from the partner's settings, or an empty string if not found.
		 */
		ehrProviderName() {
			return _get(this.partner, 'ehr_settings.provider.name', '')
		},

		/**
		 * display's the first note title in case of selected note types
		 */
		firstNoteTitleSelected() {
			const noteTypes = JSON.parse(
				_get(this.partner, 'ehr_settings.note_types', '[]')
			)

			noteTypes.sort((a, b) => {
				const nameA = a.note_name.toUpperCase()
				const nameB = b.note_name.toUpperCase()

				const isNumberA = /^\d/.test(nameA)
				const isNumberB = /^\d/.test(nameB)

				if (isNumberA && !isNumberB) return -1
				if (!isNumberA && isNumberB) return 1
				return nameA.localeCompare(nameB)
			})

			return noteTypes.length ? noteTypes[0].note_name : ''
		},

		/**
		 * Checks if the save button should be disabled based on differences between form values and EHR settings, and ensures valid credentials exist.
		 */
		isSaveDisabled() {
			//values from form
			const fromPasswordVal = this.form.ehr_config.password
			const formtDestID = this.form.ehr_config.destination_id
			const formUserName = this.form.ehr_config.username
			const formApplicationID = this.form.ehr_config
				.service_application_id
			const formFacilityID = this.form.ehr_config.service_facility_id

			//values from api
			const ehrSettingsVal = this.partner?.ehr_settings
			const apiValPass = ehrSettingsVal.password
			const apiValDest = ehrSettingsVal.destination_id
			const apiValUserName = ehrSettingsVal.username
			const apiValApplicationID = ehrSettingsVal.service_application_id
			const apiValFacilityID = ehrSettingsVal.service_facility_id

			if (
				fromPasswordVal !== apiValPass ||
				formtDestID !== apiValDest ||
				formUserName !== apiValUserName ||
				formApplicationID !== apiValApplicationID ||
				formFacilityID !== apiValFacilityID
			) {
				return
			} else {
				const ehrSettings = _get(this.partner, 'ehr_settings', {})
				const hasValidCredentials =
					(_get(ehrSettings, 'username', '') || '').trim() !== '' &&
					(_get(ehrSettings, 'password', '') || '').trim() !== '' &&
					(_get(ehrSettings, 'destination_id', '') || '').trim() !==
						'' &&
					(
						_get(ehrSettings, 'service_application_id', '') || ''
					).trim() !== '' &&
					(
						_get(ehrSettings, 'service_facility_id', '') || ''
					).trim() !== '' &&
					(_get(ehrSettings, 'note_types', '') || '').trim() !== '' &&
					this.selectedValues.length > 0
				return hasValidCredentials && !this.isNoteTypesFetchError
			}
		},

		/**
		 * Checks if the partner has EHR settings configured in the Vuex store.
		 * @returns {boolean} True if EHR settings are present; otherwise, false.
		 */
		isEhrConfigured() {
			return Boolean(_get(this.$store.getters, 'partner.ehr_settings'))
		},

		/**
		 * Returns the note types sorted alphabetically by title.
		 */
		sortedNoteTypes() {
			return this.noteTypes
				.slice()
				.sort((a, b) => a.title.localeCompare(b.title))
		},

		/**
		 * Filters and returns tabs, excluding the 'ehr' tab if EHR is not configured.
		 */
		visibleTabs() {
			return this.tabs.filter(tab => {
				if (
					tab.name === 'ehr' &&
					(!this.isEhrConfigured || !this.isPartnerAdmin)
				) {
					return false
				}
				return true
			})
		},

		/**
		 * Get the partner's name.
		 *
		 * @return {String}
		 */
		name() {
			return this.partner?.name || ''
		},

		/**
		 * Determines whether note types should be displayed, based on availability and error status.
		 */
		shouldShowNoteTypes() {
			return (
				this.noteTypes &&
				this.noteTypes.length > 0 &&
				!this.isNoteTypesFetchError
			)
		},

		/**
		 * Getter: Parses and returns fallback emails as an array of trimmed email addresses.
		 * Setter: Joins and sets the array of emails as a comma-separated string.
		 */
		fallbackEmailTags: {
			get() {
				let emails = this.form.rsm_fallback_email
				if (emails && emails.trim()) {
					return emails.split(/,|;|\s+/).filter(email => email.trim())
				}

				return []
			},
			set(newEmails) {
				this.form.rsm_fallback_email = newEmails.join(', ')
			},
		},

		/**
		 * Retrieves the partner ID from the current route's parameters and converts it to an integer.
		 */
		partnerId() {
			return parseInt(this.$route.params.partner)
		},

		isWMAEnabledPartner() {
			return this.partner?.wma_submit && this.officeOpenInfo.visible
		},

		...mapGetters({
			partner: 'partners/active',
			access: 'auth/hasOfficeManagerAccess',
		}),

		/**
		 * Email suggestions length
		 *
		 */
		shouldOpenUpwards() {
			return this.emailSuggestions.length > 2
		},
		...mapState('settings', {
			officeOpenInfo: state => state.officeOpenInfo,
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		isChecked(data) {
			const isSelected = this.selectedValues.find(value => value === data)
			return isSelected !== undefined
		},

		onNoteTypeSelected(note) {
			this.currentNoteTitle = note.title

			if (this.areAtLeastOneNoteSelected()) {
				this.showNoteWarning = false
			}
		},

		onClickNoteType(note) {
			this.currentNoteTitle = note.title
		},

		selectTab(tabName) {
			if (!this.isTabDisabled(tabName) || tabName !== this.activeTab) {
				this.fillForm()
				this.activeTab = tabName
				this.showPasscode = false
				this.wasCleared = false
			}
		},

		isTabDisabled(tabName) {
			if (tabName === this.activeTab) return false

			switch (this.activeTab) {
				case 'voicemail':
					return !this.isVoicemailComplete()
				case 'ehr':
					return !this.isEHRComplete()
				default:
					return false
			}
		},

		// no longer reqyuired for config feild
		// isConfigComplete() {
		// 	return this.form.fallback_email && this.form.fallback_phone_number
		// },

		isVoicemailComplete() {
			return this.form.passcode
		},

		isEHRComplete() {
			return (
				this.form.ehr_config.username &&
				this.form.ehr_config.password &&
				this.form.ehr_config.destination_id &&
				(this.isNoteTypesFetchError || this.areAtLeastOneNoteSelected())
			)
		},

		areAtLeastOneNoteSelected() {
			const isAnyNoteSelected = this.selectedValues.length > 0
			return isAnyNoteSelected
		},

		debounce(fn, delay) {
			let timeoutID
			return function(...args) {
				if (timeoutID) {
					clearTimeout(timeoutID)
				}
				timeoutID = setTimeout(() => {
					fn.apply(this, args)
				}, delay)
			}
		},

		/**
		 * Get the form field names/keys.
		 *
		 * @return {Array}
		 */
		fields() {
			return Object.keys(this.form)
		},

		isEHRConfiguredSwitch() {
			// return this.$store.getters?.partner?.ehr_settings
			return this.partner?.ehr_settings
		},

		isEHRValid() {
			const ehrSettings = this.partner?.ehr_settings
			return (
				ehrSettings?.username &&
				ehrSettings.username.trim() !== '' &&
				ehrSettings?.password &&
				ehrSettings.password.trim() !== '' &&
				ehrSettings?.destination_id &&
				ehrSettings.destination_id.trim() !== ''
			)
		},

		/**
		 * Fill the form with the partner's information.
		 *
		 * @return {void}
		 */
		fillForm() {
			if (!this.partner) {
				return this.reset()
			}

			this.fields().forEach(key => {
				if (
					this.partner[key] !== null &&
					typeof this.partner[key] !== 'undefined'
				) {
					if (
						[
							'office_phone_number',
							'office_fax_number',
							'fallback_phone_number',
						].includes(key)
					) {
						this.form[key] = this.partner[key].replace(/^\+1/, '')
					} else {
						this.form[key] = this.partner[key]
					}
				}
			})

			if (this.isEHRConfiguredSwitch()) {
				this.fetchCoordinationNoteTypes()
				if (this.isEHRValid()) {
					this.form.ehr_config.note_types =
						this.partner.ehr_settings.note_types || {}

					if (
						this.partner.ehr_settings.note_types &&
						!this.isNoteTypesFetchError
					) {
						try {
							const partnerNotes = JSON.parse(
								this.partner.ehr_settings.note_types
							).map(x => x.note_id)
							const partnerNotesTitlesSelected = JSON.parse(
								this.partner.ehr_settings.note_types
							).map(x => x.note_name)
							this.selectedValues = [...partnerNotes]
							this.selectedValuesForTitles = [
								...partnerNotesTitlesSelected,
							]
							this.savedSelectedValues = [...this.selectedValues]
						} catch (error) {
							console.error('Error parsing note types:', error)
							this.selectedValues = []
							this.selectedValuesForTitles = []
						}
					} else {
						this.form.ehr_config.note_types = []
						this.selectedValues = []
						this.selectedValuesForTitles = []
					}
				}
				this.form.ehr_config.username =
					this.partner.ehr_settings.username || ''
				this.form.ehr_config.password =
					this.partner.ehr_settings.password || ''
				this.form.ehr_config.destination_id =
					this.partner.ehr_settings.destination_id || ''
				this.form.ehr_config.service_application_id =
					this.partner.ehr_settings.service_application_id || ''
				this.form.ehr_config.service_facility_id =
					this.partner.ehr_settings.service_facility_id || ''
				this.form.ehr_config.metadata =
					this.partner.ehr_settings.metadata || {}
				this.form.ehr_config.note_types = []
				this.currentNoteTitle = ''
			}
			this.activeTab = 'config'
			this.showNoteWarning = false
			this.showPasscode = false
			this.wasCleared = false
			this.officeNumberErrorMessage = ''
			this.passcodeErrorMessage = ''
			this.fallbackNumberErrorMessage = ''
			this.emailErrorMessage = ''
			this.showPasscode = false
			this.form.rsm_fallback_email =
				this.partner.rsm_fallback_email ||
				this.partner.fallback_email ||
				''
		},

		/**
		 * Handle the schedule click event.
		 *
		 * @return {void}
		 */
		onScheduleClick() {
			this.panel = true
		},

		removeLastEmail() {
			if (this.fallbackEmailTags.length > 0) {
				this.fallbackEmailTags.pop()
				this.updateFormData()
			}
		},

		handleBackspace(event) {
			if (
				(event.key === 'Backspace' || event.which === 8) &&
				!this.newEmail
			) {
				this.removeLastEmail()
			}
		},

		updateFormData() {
			this.form.rsm_fallback_email = this.fallbackEmailTags.join(', ')
		},

		removeEmail(index) {
			const emails = this.fallbackEmailTags.filter((_, i) => i !== index)
			this.fallbackEmailTags = emails
			if (emails.length === 0) {
				this.form.rsm_fallback_email = null
				this.form.rsm_fallback_email = emails.join(', ')
			}
		},

		addEmail() {
			const emails = this.newEmail.split(/[;, \t\n]+/)
			emails.forEach(email => {
				if (this.validateEmail(email)) {
					const trimmedEmail = email.trim()
					if (trimmedEmail) {
						const newEmails = trimmedEmail.split(/[;, \t\n]+/)
						const existingEmails = (
							this.form.rsm_fallback_email || ''
						)
							.trim()
							.split(/[;, \t\n]+/)
							.filter(email => email.trim())
						const allEmails = [...existingEmails, ...newEmails]
						this.form.rsm_fallback_email = allEmails.join(', ')
						if (!this.form.rsm_fallback_email) {
							this.form.fallback_email = this.form.rsm_fallback_email
						}
					}
				}
			})
			this.newEmail = ''
			this.emailSuggestions = []
		},

		async fetchEmailSuggestions(searchTerm) {
			if (!searchTerm) return

			const partnerId = parseInt(this.$route.params.partner)
			const apiUrl = `${process.env.VUE_APP_API_URL}/api/v4/partners/${partnerId}/search?field=email&role=office_manager&search_value=${searchTerm}`

			try {
				const response = await fetch(apiUrl, {
					headers: {
						Authorization: `Bearer ${this.$store.state.auth.session.access_token}`,
					},
				})

				if (!response.ok) {
					throw new Error('Failed to fetch email suggestions')
				}

				const data = await response.json()

				const fallbackEmails = this.form.rsm_fallback_email
					.split(',')
					.map(email => email.trim())

				const uniqueSuggestions = data
					.map(suggestion => suggestion.trim())
					.filter(email => !fallbackEmails.includes(email))

				this.emailSuggestions = uniqueSuggestions
			} catch (error) {
				console.error(
					'fetchEmailSuggestions: Error fetching email suggestions',
					error
				)
			}
		},

		validateNoteTypes() {
			if (
				this.selectedValues.length === 0 &&
				this.noteTypes &&
				this.noteTypes.length > 0
			) {
				this.noteTypesErrorMessage =
					'At least one note type must be selected.'
				return false
			}
			this.noteTypesErrorMessage
			return true
		},

		async fetchCoordinationNoteTypes() {
			const partnerId = parseInt(this.$route.params.partner)
			const apiUrl = `${process.env.VUE_APP_API_URL}/api/v4/ehr/note-types?partner_id=${partnerId}`

			try {
				const response = await fetch(apiUrl, {
					headers: {
						Authorization: `Bearer ${this.$store.state.auth.session.access_token}`,
					},
				})

				if (!response.ok) {
					throw new Error('Failed to fetch coordination note types')
				}

				const data = await response.json()

				if (Array.isArray(data.note_types)) {
					this.noteTypes = data.note_types

					let partnerNoteTypes = []
					if (data.ehr_settings && data.ehr_settings.note_types) {
						try {
							partnerNoteTypes = JSON.parse(
								data.ehr_settings.note_types
							)
						} catch (error) {
							console.error(
								'Error parsing ehr_settings.note_types:',
								error
							)
						}
					} else {
						console.warn(
							'ehr_settings or ehr_settings.note_types is missing or empty.'
						)
					}

					this.selectedNoteTypes = this.noteTypes.filter(note =>
						partnerNoteTypes.some(
							selectedNote => selectedNote.note_id === note.id
						)
					)
					this.isNoteTypesFetchError = false
				} else {
					console.warn(
						'Note Types data is not an array or is missing.'
					)
					this.isNoteTypesFetchError = true
				}
			} catch (error) {
				console.error(
					'fetchCoordinationNoteTypes: Error fetching coordination note types',
					error
				)
				this.isNoteTypesFetchError = true
			}
		},

		clearCurrentNoteTitle() {
			this.currentNoteTitle = null
		},

		validateEmail(email) {
			if (!email) {
				this.emailErrorMessage = ''
				return true
			}

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(email)) {
				this.emailErrorMessage = 'Please enter a valid email address.'
				return false
			}

			if (this.fallbackEmailTags.includes(email)) {
				this.emailErrorMessage =
					'Duplicate email address. Please enter a unique email.'
				return false
			}

			if (/[A-Z]/.test(email)) {
				this.emailErrorMessage =
					'Email addresses cannot contain capital letters.'
				return false
			}

			this.emailErrorMessage = ''
			return true
		},

		checkForComma(event) {
			if (event.key === ',') {
				event.preventDefault()
				this.addEmail()
			}
		},

		isSequential(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					parseInt(passcode[i]) + 1 === parseInt(passcode[i + 1]) &&
					parseInt(passcode[i + 1]) + 1 === parseInt(passcode[i + 2])
				) {
					return true
				}
			}
			return false
		},

		isRepeating(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					passcode[i] === passcode[i + 1] &&
					passcode[i + 2] === passcode[i]
				) {
					return true
				}
			}
			return false
		},

		async selectEmail(suggestion) {
			const selectedEmail = suggestion

			if (this.validateEmail(selectedEmail)) {
				const newEmails = selectedEmail.split(/[;, \t\n]+/)
				const existingEmails = (this.form.rsm_fallback_email || '')
					.trim()
					.split(/[;, \t\n]+/)
					.filter(email => email.trim())
				const allEmails = [...existingEmails, ...newEmails]
				this.form.rsm_fallback_email = allEmails.join(', ')
				this.newEmail = ''
				this.emailErrorMessage = ''
			} else {
				console.log(
					'selectEmail: Email validation failed for',
					selectedEmail
				)
			}
		},

		async filterEmailSuggestions() {
			const searchTerm = this.newEmail.toLowerCase().trim()
			if (searchTerm.length >= 2) {
				this.emailSuggestions = this.allEmails.filter(email => {
					const lowerCaseEmail = email.toLowerCase()
					return (
						lowerCaseEmail.includes(searchTerm) &&
						!this.fallbackEmailTags.includes(email)
					)
				})
			} else {
				this.emailSuggestions = []
			}
		},

		debounceFilterEmailSuggestions: function(event) {
			const searchTerm = event.target.value.trim()
			this.debounce(function() {
				if (searchTerm.length >= 2) {
					this.fetchEmailSuggestions(searchTerm)
				} else {
					this.emailSuggestions = []
				}
			}, 300).apply(this)
		},

		validatePasscode() {
			const passcode = this.form.passcode

			if (!/^\d+$/.test(passcode)) {
				this.passcodeErrorMessage = 'Passcode can only contain digits.'
				return false
			}

			if (passcode.length < 6) {
				this.passcodeErrorMessage =
					'Passcode must be at least 6 digits long.'
				return false
			}

			if (this.isSequential(passcode)) {
				this.passcodeErrorMessage =
					'Passcode cannot contain 3 or more sequential digits.'
				return false
			}

			if (this.isRepeating(passcode)) {
				this.passcodeErrorMessage =
					'Passcode cannot contain 3 or more repeating digits.'
				return false
			}

			this.passcodeErrorMessage = ''
			return true
		},

		validateOfficePhoneNumber() {
			const sanitizedPhoneNumber = this.form.office_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = this.partner.twilio_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.officeNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.officeNumberErrorMessage =
					'Phone number has to be a valid 10 digit number.'
				return false
			}

			if (sanitizedPhoneNumber === sanitizedTwilioPhone) {
				this.officeNumberErrorMessage =
					'Phone number cannot be the same as your Twilio phone number.'
				return false
			}

			this.officeNumberErrorMessage = ''
			return true
		},

		validateFallbackPhoneNumber() {
			const sanitizedPhoneNumber = this.form.fallback_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = this.partner.twilio_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			const sanitizedOfficePhone = this.form.office_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.fallbackNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.fallbackNumberErrorMessage =
					'Phone number has to be a valid 10 digit number.'
				return false
			}

			if (sanitizedPhoneNumber === sanitizedTwilioPhone) {
				this.fallbackNumberErrorMessage =
					'Phone number cannot be the same as Twilio Phone or Office Phone.'
				return false
			}

			if (sanitizedPhoneNumber === sanitizedOfficePhone) {
				this.fallbackNumberErrorMessage =
					'Phone number cannot be the same as Twilio Phone or Office Phone.'
				return false
			}

			this.fallbackNumberErrorMessage = ''
			return true
		},

		getNoteTypesPayload() {
			const fromPasswordVal = this.form.ehr_config.password
			const formtDestID = this.form.ehr_config.destination_id
			const formUserName = this.form.ehr_config.username

			const ehrSettingsVal = this.partner?.ehr_settings || {}
			const apiValPass = ehrSettingsVal.password
			const apiValDest = ehrSettingsVal.destination_id
			const apiValUserName = ehrSettingsVal.username

			let noteTypesPayload

			if (
				fromPasswordVal !== apiValPass ||
				formtDestID !== apiValDest ||
				formUserName !== apiValUserName
			) {
				noteTypesPayload = []
				this.selectedValues = []
			} else {
				noteTypesPayload = this.selectedValues
					.map(noteId => {
						const note = this.noteTypes.find(n => n.id === noteId)
						if (note) {
							return {
								note_id: note.id,
								note_name: note.title,
							}
						} else {
							return null
						}
					})
					.filter(note => note !== null)
			}

			return noteTypesPayload
		},

		/**
		 * Handle the on submit event.
		 *
		 * @return {void}
		 */
		async onSubmit() {
			if (
				this.activeTab === 'ehr' &&
				this.isPartnerAdmin === true &&
				this.partner?.ehr_settings &&
				!this.areAtLeastOneNoteSelected() &&
				!this.isNoteTypesFetchError
			) {
				this.showNoteWarning = true
				this.$alert.error('At least one note type must be selected.')
				return
			} else {
				this.showNoteWarning = false
			}

			const noteTypesPayload = this.getNoteTypesPayload()

			if (!this.validatePasscode()) {
				this.$alert.error(this.passcodeErrorMessage)
				return
			}

			if (!this.validateOfficePhoneNumber()) {
				this.$alert.error(this.officeNumberErrorMessage)
				return
			}

			if (!this.validateFallbackPhoneNumber()) {
				this.$alert.error(this.fallbackNumberErrorMessage)
				return
			}

			if (!this.form.rsm_fallback_email && this.form.fallback_email) {
				this.form.rsm_fallback_email = this.form.fallback_email
			}
			if (this.form.rsm_fallback_email) {
				const emails = this.form.rsm_fallback_email
					.split(/,|;|\n|\s/)
					.map(email => email.trim())
					.filter(email => email)
				const duplicates = this.findDuplicates(emails)
				if (duplicates.length > 0) {
					this.$alert.error(
						`Duplicate emails found: ${duplicates.join(', ')}.`
					)
					return
				}

				this.form.rsm_fallback_email = emails.join(', ')
			}

			const payload = {
				partner: {
					id: this.partner.id,
					name: this.form.name,
					wma_display_name: this.form.wma_display_name,
					wma_web_link: this.form.wma_web_link,
					office_phone_number: this.form.office_phone_number,
					office_fax_number: this.form.office_fax_number,
					address_1: this.form.address_1,
					address_2: this.form.address_2,
					city: this.form.city,
					state_province: this.form.state_province,
					postal_code: this.form.postal_code,
					rsm_fallback_email: this.form.rsm_fallback_email,
					country: this.form.country,
					fallback_email: this.form.fallback_email,
					fallback_phone_number: this.form.fallback_phone_number,
					timezone: this.form.timezone,
					passcode: this.form.passcode,
				},
				ehr_config: {
					username: this.form.ehr_config.username,
					password: this.form.ehr_config.password,
					destination_id: this.form.ehr_config.destination_id,
					service_application_id: this.form.ehr_config
						.service_application_id,
					service_facility_id: this.form.ehr_config
						.service_facility_id,
					note_types: noteTypesPayload,
					metadata: this.form.ehr_config.metadata,
				},
			}

			try {
				await this.update({
					id: this.partner.id,
					fromSettings: true,
					payload: payload,
				})
				this.$alert.success('partners.success')

				this.showPasscode = false
				this.wasCleared = false

				if (this.isEHRConfiguredSwitch()) {
					this.fetchCoordinationNoteTypes()
					this.savedSelectedValues = [...this.selectedValues]
				}
			} catch (e) {
				this.$alert.error('partners.error')
			}
		},

		findDuplicates(emails) {
			const duplicates = []
			const emailMap = new Map()

			emails.forEach(email => {
				const normalizedEmail = email.toLowerCase()
				if (emailMap.has(normalizedEmail)) {
					duplicates.push(email)
				} else {
					emailMap.set(normalizedEmail, true)
				}
			})
			return duplicates
		},

		/**
		 * Reset the form back to its initial state.
		 *
		 * @return {void}
		 */
		reset() {
			this.form = getFormObject()
		},

		/**
		 * Copy the voicemail data to the clipboard.
		 *
		 * @return {void}
		 */
		copyVoicemailData() {
			if (
				!this.form.rsm_fallback_email ||
				!this.validatePasscode(this.form.passcode)
			) {
				this.$alert.error(
					'Please fill in the email notifications and enter a valid passcode to copy settings.'
				)
				return
			}

			const formattedGlobalRsmPhoneNumber = this.partner.global_rsm_phone_number
				.substring(2)
				.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
			const formattedTwilioPhoneNumber = this.partner.twilio_phone_number
				.substring(2)
				.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')

			const voicemailData = `
Secondary Voicemail Instructions

The Secondary Voicemail system ensures that all messages are captured should there be a momentary service interruption with our partners, platform, or your IVR. When a message is left, an email with instructions to retrieve the message is sent to your Email Notification recipients. The configuration and instructions for your mailbox number, passcode, and email recipients are defined in your Office Manager Dashboard, Settings under Secondary Voicemail.

Important Notes:
- Multiple users can retrieve messages at the same time.
- Clarus support does not have your passcode.
- Messages will not appear in your Office Dashboard or Clarus App at this time.

Configuration of your Secondary Voicemail service:

Update your Email Notification recipients to a list of important users who may need to be notified if a message is received, such as your phone staff or providers. This service supports more than one recipient. The initial email is copied from your fallback notification email. Click the Card icon to copy them to your clipboard for printing, share with your providers, or save them to shared contacts, as they might not be available during an interruption.

To retrieve your messages:
1. Dial the voicemail retrieval number: ${formattedGlobalRsmPhoneNumber}.
2. Enter your ten-digit mailbox number: ${formattedTwilioPhoneNumber}.
3. Enter your passcode: ${this.form.passcode}.
4. You can play and re-play each message until you have captured all the details.
5. Once you play the next message, you will not be able to listen to the previous or older messages.
                `

			navigator.clipboard
				.writeText(voicemailData)
				.then(() => {
					this.$alert.success(
						'Secondary Voicemail data copied to clipboard.'
					)
				})
				.catch(err => {
					console.error('An error occurred:', err)
					this.$alert.error(
						'Failed to copy Secondary Voicemail data.'
					)
				})
		},

		onClickInfo() {
			if (window.Intercom && intercomConfig.RSM_ARTICLE_ID) {
				window.Intercom('showArticle', intercomConfig.RSM_ARTICLE_ID)
			}
		},

		onPartnerChange(newPartner, oldPartner) {
			if (!newPartner || !oldPartner) {
				return
			}

			if (newPartner.id !== oldPartner.id) {
				this.fillForm()
			}
		},

		async onCopyWMALink() {
			const response = await util.copyToClipboard(this.form.wma_web_link)
			if (response) {
				this.$alert.success(WMA_WEBLINK_SUCCESS_MESSAGE)
			} else {
				this.$alert.error(WMA_WEBLINK_ERROR_MESSAGE)
			}
		},

		async getPartnerData() {
			try {
				await [this.getPartner(this.partner.id)]
			} catch (e) {
				this.$alert.error('default.error')
			}
		},

		...mapActions({
			update: 'partners/update',
			getPartner: 'partners/find',
		}),
	},

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'Index',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Watch the active partners.
		 */

		'form.passcode'(newVal) {
			if (newVal.length === 0) {
				this.wasCleared = true
			}
		},

		'form.ehr_config.password'(newVal) {
			if (newVal.length === 0) {
				this.wasCleared = true
			}
		},

		partner: {
			immediate: true,
			handler: 'onPartnerChange',
		},

		partners: {
			immediate: true,
			handler: 'fillForm',
		},

		/**
		 * Watch the current route for active partner changes.
		 *
		 * @param {Object} to
		 * @param {Object} from
		 * @return {void}
		 */
		$route(to, from) {
			if (to.params.partner !== from.params.partner) {
				if (!this.access) {
					return this.$router.push({ name: 'app.calls' })
				}
				this.reset()
				this.fillForm()
				this.getPartnerData()
			}
		},
	},

	/**
	 * The component's created lifecycle hook.
	 *
	 * @return {void}
	 */
	created() {
		this.getPartnerData()
		this.timezones = moment.tz.zonesForCountry('us')
		if (!this.form.rsm_fallback_email && this.form.fallback_email) {
			this.form.rsm_fallback_email = this.form.fallback_email
		}

		if (this.$store.getters.partner?.ehr_settings?.note_types) {
			const partnerNotes = JSON.parse(
				this.$store.getters.partner.ehr_settings.note_types
			).map(x => x.note_id)
			const partnerNotesTitlesSelected = JSON.parse(
				this.partner.ehr_settings.note_types
			).map(x => x.note_name)
			this.selectedValues = [...partnerNotes]
			this.selectedValuesForTitles = [...partnerNotesTitlesSelected]
		}
	},

	mounted() {
		if (this.isEHRConfiguredSwitch()) {
			this.fetchCoordinationNoteTypes()
		}
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			wasCleared: false,
			form: getFormObject(),
			panel: false,
			timezones: [],
			showPasscode: false,
			newEmail: '',
			errorMessage: '',
			passcodeErrorMessage: '',
			fallbackNumberErrorMessage: '',
			officeNumberErrorMessage: '',
			emailSuggestions: [],
			emailErrorMessage: '',
			allEmails: [],
			noteTypes: [],
			selectedNoteTypes: [],
			selectedValues: [],
			selectedValuesForTitles: [],
			savedSelectedValues: [], //// for note types saved after clicking Save
			currentNoteTitle: '',
			isNoteTypesFetchError: false,
			defaultNoteTitle: '',
			showNoteWarning: false,
			tabs: [
				{ name: 'config', label: 'Configuration' },
				{ name: 'voicemail', label: 'Secondary Voicemail' },
				{ name: 'ehr', label: 'EHR Configuration' },
			],
			activeTab: 'config',
		}
	},
}
</script>

<style scoped>
.coordination-note-box {
	border: 1px solid #ccc;
	padding: 20px;
	background-color: #f9f9f9;
	border-radius: 8px;
	width: 100%;
	margin: 20px auto;
	display: flex;
	box-sizing: border-box;
}

.section-1 {
	width: 50%;
	max-height: 300px; /* Fixed height for Section 1 */
	overflow-y: auto; /* Make Section 1 scrollable */
	padding-right: 10px;
	box-sizing: border-box;
}

.section-2 {
	width: 50%;
	border: 1px dashed #ccc; /* Visual placeholder for Section 2 */
	height: 300px; /* Match height with Section 1 for consistency */
	background-color: #fff;
}

.note-type-item {
	display: flex;
	align-items: center;
	margin-bottom: 10px;
}

input[type='checkbox'] {
	margin-right: 10px;
}

.note-label {
	width: 100%;
}
</style>
